import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { functions } from '../firebaseConfig';
import { httpsCallable } from "firebase/functions";
import ReactMarkdown from 'react-markdown';
import { usePlan } from '../contexts/PlanContext';

const SuggestedQuestions = ({ onSelectQuestion, questions }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Only render if we have questions
  if (!questions || questions.length === 0) return null;

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      {/* Header with toggle button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full p-4 flex justify-between items-center hover:bg-gray-100 transition-colors"
      >
        <span className="text-sm font-medium text-gray-600">
          Sugestões de perguntas
        </span>
        <motion.svg
          animate={{ rotate: isCollapsed ? 180 : 0 }}
          className="h-5 w-5 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>

      {/* Collapsible content */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0">
              <div className="flex flex-wrap gap-2">
                {questions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => onSelectQuestion(question)}
                    className="text-sm px-3 py-1 rounded-full bg-white border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 mr-12"
  >
    <div className="flex items-center gap-2">
      <div className="text-gray-500">Pensando</div>
      <div className="flex gap-1">
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.2 }}
          className="w-1.5 h-1.5 bg-purple-400 rounded-full"
        />
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.3 }}
          className="w-1.5 h-1.5 bg-purple-400 rounded-full"
        />
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.4 }}
          className="w-1.5 h-1.5 bg-purple-400 rounded-full"
        />
      </div>
    </div>
  </motion.div>
);

const PremiumUpgrade = ({ chatUsage, chatLimit, period }) => (
  <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg mb-4">
    <h3 className="text-lg font-semibold text-purple-700 mb-2">
      Limite de Mensagens Atingido
    </h3>
    <p className="text-gray-600 mb-2">
      Voc&#234; utilizou {chatUsage} de {chatLimit} mensagens
      {period && (
        <span className="block text-sm">
          do per&#237;odo {new Date(period.start).toLocaleDateString()}
          at&#233; {new Date(period.end).toLocaleDateString()}
        </span>
      )}
    </p>
    <button
      onClick={() => window.location.href = '/pricing'}
      className="w-full bg-purple-600 text-white rounded-full py-2 px-4 hover:bg-purple-700 transition-colors"
    >
      Ver Planos Premium
    </button>
  </div>
);

const InsightSidebar = ({ isOpen, onClose, loading: initialLoading, preloadedMessages }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(initialLoading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const [messages, setMessages] = useState(preloadedMessages || []);
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    "Me fale sobre meu signo solar",
    "Qual é a influência do meu ascendente?",
    "Como a Lua afeta minhas emoções?"
  ]);

  const { plan, limits, usage, period, isAtLimit, refreshPlan } = usePlan();
  const messagesEndRef = useRef(null);

  // Load chat history when component mounts
  useEffect(() => {
    if (preloadedMessages) {
      setMessages(preloadedMessages);
    }
  }, [preloadedMessages]);

  // Update scrollToBottom to be more reliable
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end",
      });
    }
  };

  // Add this new effect to handle scroll when messages load or sidebar opens
  useEffect(() => {
    if (!loading && isOpen && messages.length > 0) {
      // Force scroll after a small delay to ensure content is rendered
      const timer = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, isOpen, messages.length]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setIsTyping(true);

    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);

    try {
      // Send message (backend enforces limits and auto-increments counter)
      const sendChatMessage = httpsCallable(functions, 'send_chat_message');
      const chatResult = await sendChatMessage({ message: messageText });

      // Refresh plan to update usage counters
      refreshPlan();

      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: chatResult.data.response
        }]);
        setSuggestedQuestions(chatResult.data.suggestedQuestions || []);
        setIsTyping(false);
        inputRef.current?.focus();
      }, 500);

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem.' 
      }]);
      setIsTyping(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userMessage = message;
    setMessage('');
    await sendMessage(userMessage);
    // Focus the input after sending
    inputRef.current?.focus();
  };

  const handleSuggestionSelect = async (question) => {
    await sendMessage(question);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if you want
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Modificar a lógica de renderização do PremiumUpgrade
  const renderContent = () => (
    <div className="flex-grow overflow-y-auto p-6 scroll-smooth">
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-lg relative group ${
                msg.role === 'user' 
                  ? 'bg-purple-600 text-white ml-12' 
                  : 'bg-purple-50 text-gray-700 mr-12'
              }`}
            >
              <ReactMarkdown
                className="whitespace-pre-line prose prose-sm max-w-none"
                components={{
                  p: ({node, ...props}) => <p className="mb-1 last:mb-0" {...props} />,
                  strong: ({node, ...props}) => <span className="font-bold" {...props} />,
                  em: ({node, ...props}) => <span className="italic" {...props} />,
                  code: ({node, ...props}) => <code className="bg-black/10 rounded px-1" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-4 mb-1 last:mb-0 space-y-0.5" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-4 mb-1 last:mb-0 space-y-0.5" {...props} />,
                  li: ({node, ...props}) => <li className="leading-normal" {...props} />
                }}
              >
                {msg.content}
              </ReactMarkdown>
              
              {/* Moved copy button to bottom with right alignment */}
              {msg.role === 'assistant' && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => copyToClipboard(msg.content)}
                    className="p-1.5 rounded-full bg-white hover:bg-purple-50 text-gray-500 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2"
                    title="Copiar mensagem"
                  >
                    <span className="text-xs">Copiar</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-4 w-4" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" 
                      />
                    </svg>
                  </button>
                </div>
              )}
            </motion.div>
          ))}
          {/* Typing indicator inside message list */}
          {isTyping && <TypingIndicator />}
          {/* Show upgrade message immediately after the third message if in free plan */}
          {isAtLimit('chat_messages') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              <PremiumUpgrade chatUsage={usage.chat_messages} chatLimit={limits.chat_messages} period={period} />
            </motion.div>
          )}
          <div ref={messagesEndRef} className="h-px" />
        </div>
      )}
    </div>
  );

  const chatRemaining = limits.chat_messages === Infinity
    ? Infinity
    : Math.max(0, limits.chat_messages - usage.chat_messages);

  const getRemainingDisplay = () => {
    if (chatRemaining === Infinity) return `Mensagens ilimitadas no plano ${plan}`;
    return `${chatRemaining} mensagens restantes no plano ${plan}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black z-40"
          />
          
          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 20 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-purple-600 to-pink-600">
              <h2 className="text-xl font-bold text-white">Insights Astrológicos</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modified Content Area */}
            {renderContent()}

            {/* Suggested Questions - Only show if user hasn't reached message limit */}
            {!isAtLimit('chat_messages') && (
              <SuggestedQuestions 
                onSelectQuestion={handleSuggestionSelect} 
                questions={suggestedQuestions} 
              />
            )}

            {/* Chat Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isSubmitting || isAtLimit('chat_messages')}
                  placeholder={isAtLimit('chat_messages') ?
                    "Limite de mensagens atingido" :
                    "Digite sua pergunta..."
                  }
                  className="flex-grow px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50"
                />
                <button
                  type="submit"
                  disabled={isSubmitting || !message.trim() || isAtLimit('chat_messages')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:bg-purple-400"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
              
              {!isAtLimit('chat_messages') && (
                <div className="mt-2 text-xs text-gray-500 text-center">
                  {getRemainingDisplay()
                  }
                </div>
              )}
            </form>

          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InsightSidebar;
